import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

export interface CardProps {
  children: ReactNode;
  variant?: 'default' | 'outlined' | 'elevated';
  padding?: 'none' | 'small' | 'medium' | 'large';
  hoverable?: boolean;
  clickable?: boolean;
  fullHeight?: boolean;
  className?: string;
  onClick?: () => void;
}

interface StyledCardProps {
  $variant?: CardProps['variant'];
  $padding?: CardProps['padding'];
  $hoverable?: boolean;
  $clickable?: boolean;
  $fullHeight?: boolean;
}

const getVariantStyles = (variant: CardProps['variant']): any => {
  const variants = {
    default: css`
      background-color: ${({ theme }) => theme.colors.background.paper};
      border: 1px solid
        ${({ theme }) =>
          theme.mode === 'dark'
            ? theme.colors.neutral[700]
            : theme.colors.divider};
    `,
    outlined: css`
      background-color: transparent;
      border: 1px solid
        ${({ theme }) =>
          theme.mode === 'dark'
            ? theme.colors.neutral[600]
            : theme.colors.divider};
    `,
    elevated: css`
      background-color: ${({ theme }) => theme.colors.background.paper};
      border: none;
      box-shadow: ${({ theme }) => theme.shadows.md};
    `,
  };

  return variants[variant || 'default'];
};

const getPaddingStyles = (padding: CardProps['padding']): any => {
  const paddings = {
    none: css`
      padding: 0;
    `,
    small: css`
      padding: ${({ theme }) => theme.spacing[3]};
    `,
    medium: css`
      padding: ${({ theme }) => theme.spacing[4]};
    `,
    large: css`
      padding: ${({ theme }) => theme.spacing[6]};
    `,
  };

  return paddings[padding || 'medium'];
};

const StyledCard = styled.div<StyledCardProps>`
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  transition: all ${({ theme }) => theme.transitions.duration.base}
    ${({ theme }) => theme.transitions.easing.inOut};
  position: relative;
  overflow: hidden;

  ${({ $variant }) => getVariantStyles($variant)}
  ${({ $padding }) => getPaddingStyles($padding)}

  ${({ $fullHeight }) =>
    $fullHeight &&
    css`
      height: 100%;
    `}

  ${({ $hoverable }) =>
    $hoverable &&
    css`
      &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => theme.shadows.lg};
      }
    `}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      user-select: none;

      &:active {
        transform: scale(0.98);
      }
    `}
`;

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  padding = 'medium',
  hoverable = false,
  clickable = false,
  fullHeight = false,
  className,
  onClick,
}) => {
  return (
    <StyledCard
      $variant={variant}
      $padding={padding}
      $hoverable={hoverable}
      $clickable={clickable || !!onClick}
      $fullHeight={fullHeight}
      className={className}
      onClick={onClick}
    >
      {children}
    </StyledCard>
  );
};

export const CardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};

  h2,
  h3,
  h4 {
    margin: 0;
  }
`;

export const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
`;

export const CardFooter = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

export default Card;
